// src/pages/SshManager.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Terminal, Plus, Edit2, Trash2, RefreshCw, Search, X, Save, Play, CheckCircle, AlertTriangle, Star, Eye, EyeOff, Copy } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

function ConnectionModal({ conn, clients, onClose, onSaved }) {
  const isEdit = !!conn?.id;
  const [f, setF] = useState(isEdit ? { ...conn, password: '', private_key: '' } : {
    name: '', host: '', port: 22, username: 'root', auth_type: 'password',
    password: '', private_key: '', passphrase: '', group_name: '', client_id: '', notes: '', is_favorite: false
  });
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name || !f.host || !f.username) return toast.error('Nume, host și username sunt obligatorii');
    setLoading(true);
    try {
      if (isEdit) { await api.put(`/ssh/connections/${conn.id}`, f); toast.success('Conexiune actualizată'); }
      else { await api.post('/ssh/connections', f); toast.success('Conexiune adăugată'); }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <span className="modal-title"><Terminal size={16} /> {isEdit ? conn.name : 'Conexiune SSH Nouă'}</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group"><label className="form-label">Nume *</label><input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} placeholder="Production Server" /></div>
            <div className="form-group"><label className="form-label">Host / IP *</label><input className="form-input" value={f.host} onChange={e => set('host', e.target.value)} placeholder="192.168.1.1 sau server.com" /></div>
            <div className="form-group"><label className="form-label">Port</label><input className="form-input" type="number" value={f.port} onChange={e => set('port', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Username *</label><input className="form-input" value={f.username} onChange={e => set('username', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Tip autentificare</label>
              <select className="form-select" value={f.auth_type} onChange={e => set('auth_type', e.target.value)}>
                <option value="password">Parolă</option>
                <option value="key">Cheie privată</option>
                <option value="agent">SSH Agent</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Grup</label><input className="form-input" value={f.group_name || ''} onChange={e => set('group_name', e.target.value)} placeholder="Production, Staging..." /></div>
            {f.auth_type === 'password' && (
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Parolă {isEdit && '(gol = păstrează)'}</label>
                <div style={{ position: 'relative' }}>
                  <input className="form-input" type={showPwd ? 'text' : 'password'} value={f.password} onChange={e => set('password', e.target.value)} style={{ paddingRight: 36 }} />
                  <button style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => setShowPwd(p => !p)}>
                    {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            )}
            {f.auth_type === 'key' && (
              <>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Cheie Privată (PEM) {isEdit && '(gol = păstrează)'}</label>
                  <textarea className="form-textarea" style={{ fontFamily: 'monospace', fontSize: 11, minHeight: 100 }} value={f.private_key} onChange={e => set('private_key', e.target.value)} placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Passphrase (opțional)</label>
                  <input className="form-input" type="password" value={f.passphrase || ''} onChange={e => set('passphrase', e.target.value)} />
                </div>
              </>
            )}
            <div className="form-group"><label className="form-label">Client</label>
              <select className="form-select" value={f.client_id || ''} onChange={e => set('client_id', e.target.value)}>
                <option value="">— Fără client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.company_name || c.contact_name}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Notițe</label><textarea className="form-textarea" value={f.notes || ''} onChange={e => set('notes', e.target.value)} /></div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={!!f.is_favorite} onChange={e => set('is_favorite', e.target.checked)} />
            <Star size={14} color="#f59e0b" /> Marchează ca favorit
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={save} disabled={loading}>{loading ? <span className="spinner" /> : <><Save size={14} /> {isEdit ? 'Actualizare' : 'Adăugare'}</>}</button>
        </div>
      </div>
    </div>
  );
}

function TerminalModal({ conn, onClose }) {
  const [history, setHistory] = useState([{ type: 'info', text: `Conectat la ${conn.username}@${conn.host}:${conn.port} — tastați comenzi` }]);
  const [cmd, setCmd] = useState('');
  const [cmdHist, setCmdHist] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [running, setRunning] = useState(false);
  const termRef = useRef(null);
  const toast = useToast();

  const run = async () => {
    if (!cmd.trim()) return;
    const c = cmd.trim();
    setCmd(''); setCmdHist(h => [c, ...h.slice(0, 49)]); setHistIdx(-1);
    setHistory(h => [...h, { type: 'cmd', text: c }]);
    setRunning(true);
    try {
      const { data } = await api.post(`/ssh/connections/${conn.id}/exec`, { cmd: c });
      if (data.stdout) setHistory(h => [...h, { type: 'out', text: data.stdout }]);
      if (data.stderr) setHistory(h => [...h, { type: 'err', text: data.stderr }]);
      if (!data.stdout && !data.stderr) setHistory(h => [...h, { type: 'info', text: '(no output)' }]);
    } catch (e) {
      setHistory(h => [...h, { type: 'err', text: e.response?.data?.error || 'Eroare conexiune' }]);
    } finally {
      setRunning(false);
      setTimeout(() => { if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight; }, 50);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter') run();
    else if (e.key === 'ArrowUp') { e.preventDefault(); const i = Math.min(histIdx + 1, cmdHist.length - 1); setHistIdx(i); setCmd(cmdHist[i] || ''); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); const i = Math.max(histIdx - 1, -1); setHistIdx(i); setCmd(i < 0 ? '' : cmdHist[i]); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 800, width: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <span className="modal-title" style={{ fontFamily: 'monospace' }}>
            <Terminal size={16} /> {conn.username}@{conn.host}:{conn.port}
          </span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div ref={termRef} style={{ flex: 1, background: '#0d1117', borderRadius: 8, padding: 14, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6, overflowY: 'auto', minHeight: 300, maxHeight: 450, border: '1px solid var(--border)' }}>
            {history.map((h, i) => (
              <div key={i} style={{ color: h.type === 'cmd' ? '#79c0ff' : h.type === 'err' ? '#ff6b6b' : h.type === 'info' ? '#666' : '#c9d1d9', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {h.type === 'cmd' && <span style={{ color: '#3fb950' }}>{conn.username}@{conn.host} $ </span>}
                {h.text}
              </div>
            ))}
            {running && <div style={{ color: '#666' }}>Rulează...</div>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#3fb950', alignSelf: 'center' }}>$</span>
            <input className="form-input" style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, background: '#0d1117', color: '#c9d1d9', border: '1px solid var(--border)' }}
              value={cmd} onChange={e => setCmd(e.target.value)} onKeyDown={onKey} disabled={running} autoFocus placeholder="Enter command..." />
            <button className="btn btn-primary" onClick={run} disabled={running || !cmd.trim()}>Run</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SshManager() {
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [modal, setModal] = useState(null);
  const [termConn, setTermConn] = useState(null);
  const [testing, setTesting] = useState({});
  const toast = useToast();
  const { hasRole } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, cl, g] = await Promise.all([
        api.get('/ssh/connections', { params: { search: search || undefined, group_name: filterGroup || undefined } }),
        api.get('/clients'),
        api.get('/ssh/groups'),
      ]);
      setList(c.data); setClients(cl.data); setGroups(g.data);
    } catch (e) { toast.error('Eroare la încărcare'); }
    finally { setLoading(false); }
  }, [search, filterGroup]);

  useEffect(() => { load(); }, [load]);

  const test = async (id, name) => {
    setTesting(p => ({ ...p, [id]: true }));
    try {
      const { data } = await api.post(`/ssh/connections/${id}/test`);
      if (data.success) toast.success(`${name}: ${data.message}`);
      else toast.error(`${name}: ${data.message}`);
    } catch (e) { toast.error('Eroare test'); }
    finally { setTesting(p => ({ ...p, [id]: false })); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Ștergi conexiunea "${name}"?`)) return;
    try { await api.delete(`/ssh/connections/${id}`); toast.success('Conexiune ștearsă'); load(); }
    catch (e) { toast.error('Eroare'); }
  };

  const copyCmd = (c) => {
    navigator.clipboard.writeText(`ssh ${c.username}@${c.host} -p ${c.port}`);
    toast.success('Comandă SSH copiată');
  };

  const grouped = list.reduce((acc, c) => {
    const g = c.group_name || 'Fără grup';
    if (!acc[g]) acc[g] = [];
    acc[g].push(c);
    return acc;
  }, {});

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><div className="page-title">SSH Manager</div><div className="page-subtitle">Gestiune conexiuni SSH și terminal web</div></div>
        <button className="btn btn-primary" onClick={() => setModal({ new: true })}><Plus size={14} /> Conexiune Nouă</button>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută server, IP..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="form-select" style={{ width: 160 }} value={filterGroup} onChange={e => setFilterGroup(e.target.value)}>
            <option value="">Toate grupurile</option>
            {groups.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={load}><RefreshCw size={14} /></button>
        </div>

        {loading ? <div className="loading-center"><span className="spinner" /></div> : list.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
            <Terminal size={36} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }} />
            Nicio conexiune SSH
          </div>
        ) : (
          Object.entries(grouped).map(([group, conns]) => (
            <div key={group} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 8, padding: '0 4px' }}>{group}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10 }}>
                {conns.map(c => (
                  <div key={c.id} style={{ background: 'var(--bg-hover)', borderRadius: 10, padding: '12px 14px', border: '1px solid var(--border)', position: 'relative' }}>
                    {c.is_favorite && <Star size={12} color="#f59e0b" style={{ position: 'absolute', top: 10, right: 10 }} />}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <div style={{ width: 36, height: 36, background: 'var(--accent)20', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Terminal size={18} color="var(--accent)" />
                      </div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{c.name}</div>
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>{c.username}@{c.host}:{c.port}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{c.auth_type}</span>
                      {c.company_name && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: 'var(--bg-primary)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}>{c.company_name}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 10 }}>
                      <button className="btn btn-primary" style={{ flex: 1, fontSize: 12 }} onClick={() => setTermConn(c)}><Terminal size={13} /> Terminal</button>
                      <button className="btn btn-secondary" style={{ fontSize: 12 }} title="Test conexiune" onClick={() => test(c.id, c.name)} disabled={testing[c.id]}>
                        {testing[c.id] ? <span className="spinner" style={{ width: 13, height: 13 }} /> : <Play size={13} />}
                      </button>
                      <button className="btn btn-secondary" style={{ fontSize: 12 }} title="Copiază comandă SSH" onClick={() => copyCmd(c)}><Copy size={13} /></button>
                      <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => api.get(`/ssh/connections/${c.id}`).then(r => setModal(r.data))}><Edit2 size={13} /></button>
                      {hasRole('superadmin', 'admin') && <button className="btn-icon" style={{ color: 'var(--danger)' }} onClick={() => del(c.id, c.name)}><Trash2 size={13} /></button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {modal && <ConnectionModal conn={modal.new ? null : modal} clients={clients} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {termConn && <TerminalModal conn={termConn} onClose={() => setTermConn(null)} />}
    </div>
  );
}
